import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const TD_STYLES = {
  REQUESTED: "border-blue-500/30 bg-blue-500/10 text-blue-400",
  OPS_APPROVED: "border-purple-500/30 bg-purple-500/10 text-purple-400",
  TAKEN_DOWN: "border-rose-500/30 bg-rose-500/10 text-rose-400",
  REJECTED: "border-slate-500/30 bg-slate-500/10 text-slate-400",
};

function TdBadge({ s }) {
  return <Badge variant="outline" className={TD_STYLES[s] || ""}>{s.replace(/_/g, " ")}</Badge>;
}

export default function Takedowns() {
  const { user } = useAuth();
  const canRequest = ["label", "label_manager", "artist", "super_admin"].includes(user.role);
  const canOps = ["operations", "super_admin"].includes(user.role);
  const canFinal = user.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [releases, setReleases] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ release_id: "", reason: "" });

  const load = () => api.get("/takedowns").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    if (canRequest) api.get("/releases").then((r) => setReleases(r.data.filter((x) => ["APPROVED", "LIVE"].includes(x.status))));
  }, [canRequest]);

  const create = async () => {
    if (!f.release_id || !f.reason) return toast.error("Select a release and provide a reason");
    try {
      await api.post("/takedowns", f);
      toast.success("Takedown requested");
      setOpen(false);
      setF({ release_id: "", reason: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const opsReview = async (id, status) => {
    await api.post(`/takedowns/${id}/ops-review`, { status });
    toast.success(status === "OPS_APPROVED" ? "Approved — sent to Super Admin" : "Rejected");
    load();
  };
  const finalize = async (id, status) => {
    await api.post(`/takedowns/${id}/finalize`, { status });
    toast.success(status === "TAKEN_DOWN" ? "Release taken down" : "Rejected");
    load();
  };

  return (
    <div className="space-y-6" data-testid="takedowns-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight">Takedown Requests</h1><p className="mt-1 text-muted-foreground">Request removal of approved/live releases. Ops reviews, then Super Admin finalizes.</p></div>
        {canRequest && <Button onClick={() => setOpen(true)} data-testid="new-takedown-btn"><Plus className="mr-2 h-4 w-4" />Request Takedown</Button>}
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Release</TableHead><TableHead>Requested By</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No takedown requests.</TableCell></TableRow>}
            {rows.map((t) => (
              <TableRow key={t.id} data-testid={`takedown-row-${t.id}`}>
                <TableCell className="font-medium">{t.release_title}</TableCell>
                <TableCell>{t.requested_by_name}</TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground">{t.reason}</TableCell>
                <TableCell><TdBadge s={t.status} /></TableCell>
                <TableCell className="text-right">
                  {canOps && t.status === "REQUESTED" && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" onClick={() => opsReview(t.id, "OPS_APPROVED")} data-testid={`ops-approve-${t.id}`}>Ops Approve</Button>
                      <Button size="sm" variant="destructive" onClick={() => opsReview(t.id, "REJECTED")} data-testid={`ops-reject-${t.id}`}>Reject</Button>
                    </div>
                  )}
                  {canFinal && t.status === "OPS_APPROVED" && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="destructive" onClick={() => finalize(t.id, "TAKEN_DOWN")} data-testid={`finalize-takedown-${t.id}`}>Confirm Takedown</Button>
                      <Button size="sm" variant="secondary" onClick={() => finalize(t.id, "REJECTED")} data-testid={`finalize-reject-${t.id}`}>Reject</Button>
                    </div>
                  )}
                  {["TAKEN_DOWN", "REJECTED"].includes(t.status) && <span className="text-xs text-muted-foreground">Closed</span>}
                  {t.status === "OPS_APPROVED" && !canFinal && <span className="text-xs text-muted-foreground">Awaiting Super Admin</span>}
                  {t.status === "REQUESTED" && !canOps && <span className="text-xs text-muted-foreground">Awaiting Ops</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" />Request Takedown</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Release (approved/live only)</Label>
              <Select value={f.release_id} onValueChange={(v) => setF({ ...f, release_id: v })}>
                <SelectTrigger className="mt-1" data-testid="takedown-release"><SelectValue placeholder="Select release" /></SelectTrigger>
                <SelectContent>{releases.map((r) => <SelectItem key={r.id} value={r.id}>{r.title} ({r.status})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="takedown-reason" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="submit-takedown-btn">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
